import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { Button } from '@/components/atoms'
import { attestationService } from '@/services/attestationService'
import {
  KybForbiddenError,
  KybLevelUnresolvedError,
  kybService,
} from '@/services/kybService'
import { organizationService } from '@/services/organizationService'
import { useMyOrganizations, useMyOrgRole } from '@/hooks/useOrganization'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import type { KybLinkResponse, PendingAttestation } from '@/types/kyb'
import { LEGAL_FORMS } from './institutionalFields'
import { BODY_TEXT, CARD_SHELL, ERROR_TEXT, HELP_TEXT, PAGE_TITLE } from './styles'

interface KybPendingPanelProps {
  /** The normalized, non-null `pending_attestation` from the status endpoint. */
  pendingAttestation: PendingAttestation
  /**
   * Go back into the app.
   *
   * Onboarding is two phases now, and a PENDING_VERIFICATION attestation is
   * enough to browse (see `useAccessTier`). Waiting on a KYB review is no
   * longer a dead end, so this panel offers a way out. Absent when there is
   * nowhere to go.
   */
  onBrowse?: () => void
  /** Go back to the phase-2 steps to change something. */
  onEdit?: () => void
}

/**
 * Terminal state for a submitted attestation that is waiting on verification:
 * the email-domain challenge, the Sumsub KYB review, or both.
 *
 * 🔴 This is still NOT a success screen — the entity is not verified and
 * cannot trade — but it is no longer a dead end. Since the two-phase
 * onboarding landed, a PENDING_VERIFICATION attestation earns the `browse`
 * tier, so `ProtectedRoute` lets the user into the app with financial data
 * gated. `onBrowse` is that door. Before this change any in-app navigation
 * from here was bounced straight back, which is why the only way forward used
 * to be a full-document reload.
 *
 * There is still no polling anywhere in this app. Do not add any here.
 *
 * 🔴 Role gating: `pending_attestation` is INHERITED through membership, so an
 * invited MEMBER of a pending org lands here too — but the KYB link endpoint
 * (and the domain re-claim) answer 403 for MEMBERs. Every mutating action is
 * gated on `canManage`, and an unexpected 403 is rendered as the same
 * "an owner or admin must do this" message, never as a retryable error.
 */
export const KybPendingPanel = ({
  pendingAttestation,
  onBrowse,
  onEdit,
}: KybPendingPanelProps) => {
  // Held in state so "check again" can refresh the panel with the server's
  // latest snapshot without a reload.
  const [pending, setPending] = useState<PendingAttestation>(pendingAttestation)
  const [link, setLink] = useState<KybLinkResponse | null>(null)
  const [minting, setMinting] = useState(false)
  const [checking, setChecking] = useState(false)
  const [resending, setResending] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [memberBlocked, setMemberBlocked] = useState(false)
  const [legalFormNeeded, setLegalFormNeeded] = useState(false)
  const [legalForm, setLegalForm] = useState('')
  const [savingLegalForm, setSavingLegalForm] = useState(false)

  const orgId = pending.organization_id
  const {
    organizations,
    isLoading: orgsLoading,
    refetch: refetchOrganizations,
  } = useMyOrganizations()
  const organization = orgId
    ? (organizations.find(entry => entry.organization.id === orgId)?.organization ?? null)
    : null
  const { canManage, isLoading: roleLoading } = useMyOrgRole(orgId ?? '')

  const kybStatus = pending.kyb_status
  const rejected = kybStatus === 'rejected'
  const approved = kybStatus === 'approved'

  // A MEMBER may not drive the verification; neither may anyone the endpoints
  // refused. While the role is still loading we show no mutating action at all
  // rather than flash one that may be about to disappear.
  const showManageActions = !!orgId && !roleLoading && canManage && !memberBlocked
  const showMemberNotice = !!orgId && ((!roleLoading && !canManage) || memberBlocked)

  // "Start/Resume verification" applies only to states the user can act on:
  // never for a terminal rejection, an already-approved check, a review that is
  // out of their hands, or while the missing-legal-form message is showing.
  const showResumeButton =
    showManageActions &&
    !rejected &&
    !approved &&
    !legalFormNeeded &&
    kybStatus !== 'pending_review'

  const clearFeedback = () => {
    setNotice(null)
    setError(null)
  }

  const handleRecheck = async () => {
    setChecking(true)
    clearFeedback()
    try {
      const status = await attestationService.getAttestationStatus()
      if (status.has_attestation) {
        // A full reload, so the route guards re-read the server's answer rather
        // than trusting state this component happens to hold.
        window.location.href = '/profile-preferences'
        return
      }
      if (status.pending_attestation) {
        setPending(status.pending_attestation)
        // The domain checklist row reads the organization record too, and that
        // list query is cached for 5 minutes. Refresh it alongside the pending
        // snapshot so "check again" cannot answer from a stale org.
        void refetchOrganizations()
        // A refreshed snapshot may invalidate the missing-legal-form message
        // (someone set it elsewhere); clearing it re-enables the resume button,
        // and a retry simply shows the message again if nothing changed —
        // user-driven, so no loop.
        setLegalFormNeeded(false)
        setNotice('Status refreshed — verification is not complete yet.')
        return
      }
      // No attestation and nothing pending any more: this panel no longer
      // applies (the submission was superseded or reset server-side). A full
      // reload of /attestation re-runs the server-first check and routes to
      // whatever is now correct — never a client-side navigate into the app.
      window.location.href = '/attestation'
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check your status')
    } finally {
      setChecking(false)
    }
  }

  const handleMintLink = async () => {
    if (!orgId) return
    setMinting(true)
    clearFeedback()
    try {
      const result = await kybService.createLink(orgId)
      setLink(result)
      setNotice('Your secure verification link is ready.')
    } catch (err) {
      if (err instanceof KybLevelUnresolvedError) {
        // Recoverable, not a dead end: the org's legal form is missing, so the
        // backend cannot pick a verification level.
        setLegalFormNeeded(true)
        return
      }
      if (err instanceof KybForbiddenError) {
        // A deliberate 403 — render the role message, never a retryable error.
        setMemberBlocked(true)
        return
      }
      setError(err instanceof Error ? err.message : 'Could not create a verification link')
    } finally {
      setMinting(false)
    }
  }

  /**
   * The 409 `kyb_level_unresolved` recovery, in place. A gate-blocked user
   * cannot reach the organization profile page (`ProtectedRoute` bounces
   * everything back to /attestation), so the missing `legal_form` is collected
   * right here and the link mint retried. The backend only refuses a
   * `legal_form` write while an active verification exists — and the 409 means
   * none was ever created, so this write is always permitted.
   */
  const handleSaveLegalForm = async () => {
    if (!orgId || !legalForm) return
    setSavingLegalForm(true)
    clearFeedback()
    try {
      await organizationService.updateOrganization(orgId, { legal_form: legalForm })
      setLegalFormNeeded(false)
      // Retry the mint now that the backend can resolve the level.
      await handleMintLink()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the legal form')
    } finally {
      setSavingLegalForm(false)
    }
  }

  const handleResend = async () => {
    if (!orgId || !organization?.email_domain) return
    setResending(true)
    clearFeedback()
    try {
      // Re-claiming the same domain mints a fresh token and re-sends.
      const result = await organizationService.claimEmailDomain(orgId, {
        email_domain: organization.email_domain,
      })
      setNotice(
        result.verification_email_sent
          ? 'Verification email sent again.'
          : 'We could not send a new email — no verification address is on file for this domain. Contact support and we will sort it out.',
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the verification email')
    } finally {
      setResending(false)
    }
  }

  // --- checklist ----------------------------------------------------------
  // 🔴 Three distinct visual states, never collapsed: done (green), waiting
  // (amber), failed (error red-orange). Pending must not look like approved.

  /**
   * 🔴 `pending.email_domain_verified` does NOT mean "this org verified a
   * domain". It reports whether the domain PRECONDITION is satisfied, and the
   * backend's `build_pending_attestation` satisfies it two different ways:
   *
   *     email_domain_verified = organization.email_domain is None
   *                             or bool(organization.email_domain_verified)
   *
   * so an org that claimed NO domain reports `true` — it has nothing
   * outstanding on the domain side. Reading the flag as "a domain was verified"
   * showed a green "Email domain verified" tick to orgs whose `email_domain` is
   * null, and left the "none claimed" wording below unreachable.
   *
   * The flag alone decides GREEN vs WAITING, because that is exactly what it
   * means. The organization record only WORDS a satisfied precondition; it can
   * never award the tick. `organization` comes from a list query cached for 5
   * minutes, so letting a stale record out-vote the server would paint a green
   * tick over a step the server still says is outstanding.
   *
   * `organization` is also null until that query resolves, and the two
   * satisfied cases are indistinguishable until it does — so the row is HELD
   * rather than rendered with a vaguer wording that is about to be replaced.
   * That is the same rule the mutating actions above follow: show nothing
   * rather than flash something that is about to change. The generic wording
   * survives only for a settled list that cannot name this org (a failed
   * `GET /organizations`), where it is the most we can truthfully say.
   *
   * An UNSATISFIED precondition needs none of this: the backend reports `false`
   * only when a domain is claimed and still unproven, so the waiting state is
   * correct even with no organization record.
   */
  const domainItem = (() => {
    if (orgsLoading) return null
    if (pending.email_domain_verified) {
      const done = <CheckCircle2 size={18} className="mt-[1px] shrink-0 text-[#1fb845]" />
      if (organization && !organization.email_domain) {
        return { icon: done, label: 'Email domain — none claimed, not required.' }
      }
      return {
        icon: done,
        label: organization ? 'Email domain verified.' : 'Email domain — no action needed.',
      }
    }
    return {
      icon: <Clock size={18} className="mt-[1px] shrink-0 text-[#ff540c]" />,
      label: `Email domain — waiting for someone at ${
        organization?.email_domain ?? 'your claimed domain'
      } to open the verification link we emailed.`,
    }
  })()

  const kybItem = (() => {
    switch (kybStatus) {
      case 'approved':
        return {
          icon: <CheckCircle2 size={18} className="mt-[1px] shrink-0 text-[#1fb845]" />,
          label: 'Business verification approved.',
        }
      case 'rejected':
        return {
          icon: <XCircle size={18} className="mt-[1px] shrink-0 text-[#ff540c]" />,
          label: 'Business verification unsuccessful.',
        }
      case 'retry':
        return {
          icon: <AlertTriangle size={18} className="mt-[1px] shrink-0 text-[#ff540c]" />,
          label:
            'Business verification — action needed: some information must be corrected and resubmitted.',
        }
      case 'pending_review':
        return {
          icon: <Clock size={18} className="mt-[1px] shrink-0 text-[#ff540c]" />,
          label: 'Business verification — submitted and under review.',
        }
      case 'in_progress':
        return {
          icon: <Clock size={18} className="mt-[1px] shrink-0 text-[#ff540c]" />,
          label: 'Business verification — started but not finished.',
        }
      default:
        return {
          icon: <Clock size={18} className="mt-[1px] shrink-0 text-[#ff540c]" />,
          label: 'Business verification — not started.',
        }
    }
  })()

  // --- copy ---------------------------------------------------------------

  const title = rejected ? 'Verification unsuccessful' : 'Verification pending'
  const HeaderIcon = rejected ? XCircle : approved ? CheckCircle2 : Clock
  const headerIconColor = approved && !rejected ? 'text-[#1fb845]' : 'text-[#ff540c]'

  const bodyCopy = (() => {
    if (!orgId) {
      // Defensive: the backend today only reports ORGANIZATION subjects here,
      // but the type allows an individual. No org means no KYB action to offer.
      return 'Your attestation has been submitted and is being reviewed. We will open your account as soon as the review completes.'
    }
    switch (kybStatus) {
      case 'rejected':
        return 'We could not verify your organization. Your account cannot be opened on this application.'
      case 'approved':
        return pending.email_domain_verified
          ? 'Your organization passed verification and your account is being finalized. This usually takes a moment.'
          : 'Your organization passed verification. The email-domain step below is still outstanding — your account opens as soon as it completes.'
      case 'pending_review':
        return 'We are reviewing your organization. You will be able to enter the app as soon as every item below is complete.'
      case 'retry':
        return 'Our verification partner needs some information corrected. Resume verification to fix and resubmit it.'
      case 'in_progress':
        return 'Your business verification was started but not finished. Resume it below to continue where you left off.'
      default:
        return 'Your institutional attestation has been submitted. Business verification has not been started yet — it is required before your account opens.'
    }
  })()

  return (
    <div className="min-h-screen bg-background px-4 py-12 sm:px-8">
      <div className="mx-auto w-full max-w-[620px]">
        <img
          src={logoSecondLane}
          alt="SecondLane"
          className="mb-10 h-6 w-24 dark:brightness-0 dark:invert"
        />
        <div className={cn(CARD_SHELL, 'px-6 py-10 sm:px-10')}>
          <HeaderIcon size={40} className={cn('mb-4', headerIconColor)} />
          <h1 className={PAGE_TITLE}>{title}</h1>
          <p className={cn(BODY_TEXT, 'mt-3')}>{bodyCopy}</p>

          {orgId && (
            <ul className="mt-6 flex flex-col gap-3">
              {domainItem && (
                <li className="flex items-start gap-3">
                  {domainItem.icon}
                  <span className={BODY_TEXT}>{domainItem.label}</span>
                </li>
              )}
              <li className="flex items-start gap-3">
                {kybItem.icon}
                <span className={BODY_TEXT}>{kybItem.label}</span>
              </li>
            </ul>
          )}

          {pending.kyb_moderation_comment &&
            (kybStatus === 'retry' || rejected || kybStatus === 'in_progress') && (
              <p className={cn(BODY_TEXT, 'mt-4')}>
                Note from the review: {pending.kyb_moderation_comment}
              </p>
            )}

          {rejected && (
            <p className={cn(HELP_TEXT, 'mt-3')}>
              If you believe this is a mistake, contact support and we will look into it.
            </p>
          )}

          {showMemberNotice && (
            <p className={cn(BODY_TEXT, 'mt-4')}>
              An organization owner or admin must complete the verification steps. You will get
              access automatically once they have finished.
            </p>
          )}

          {legalFormNeeded && (
            <div className="mt-4">
              <p className={BODY_TEXT}>
                Tell us your organization&apos;s legal form first — we need it to run the right
                checks.
              </p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                <Select value={legalForm} onValueChange={setLegalForm}>
                  <SelectTrigger
                    id="kyb-legal-form"
                    aria-label="Legal form"
                    className="h-[44px] font-plus-jakarta text-[14px] sm:w-[260px]"
                  >
                    <SelectValue placeholder="Select legal form" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_FORMS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveLegalForm}
                  isLoading={savingLegalForm || minting}
                  disabled={!legalForm}
                >
                  Save and start verification
                </Button>
              </div>
            </div>
          )}

          {link && (
            <p className={cn(BODY_TEXT, 'mt-4')}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Open the verification page
              </a>{' '}
              — the link is single-use and expires in 3 days. Come back here and check again when
              you are done.
            </p>
          )}

          {notice && <p className={cn(BODY_TEXT, 'mt-4 text-[#1fb845]')}>{notice}</p>}
          {error && <p className={cn(ERROR_TEXT, 'mt-4')}>{error}</p>}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            {showResumeButton && (
              <Button type="button" variant="primary" onClick={handleMintLink} isLoading={minting}>
                {kybStatus === null ? 'Start verification' : 'Resume verification'}
              </Button>
            )}
            <Button
              type="button"
              variant={showResumeButton ? 'outline' : 'primary'}
              onClick={handleRecheck}
              isLoading={checking}
            >
              I&apos;ve completed verification — check again
            </Button>
            {showManageActions && !pending.email_domain_verified && organization?.email_domain && (
              <Button type="button" variant="outline" onClick={handleResend} isLoading={resending}>
                Resend the email
              </Button>
            )}
            {onEdit && (
              <Button type="button" variant="outline" onClick={onEdit}>
                Change my details
              </Button>
            )}
            {onBrowse && (
              <Button type="button" variant="outline" onClick={onBrowse}>
                Browse SecondLane
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
